import { normalizeTools } from './agentConfigModel';

/**
 * Placeholder shown in the Manual Tool Discovery textarea (Figma 3926-8787):
 * OpenAI Responses-style function tool definition.
 */
export const MANUAL_TOOLS_PLACEHOLDER = `# Define a list of callable tools for the model
[
    {
        "type": "function",
        "name": "get_horoscope",
        "description": "Get today's horoscope for an astrological sign.",
        "parameters": {
            "type": "object",
            "properties": {
                "sign": {
                    "type": "string",
                    "description": "An astrological sign like Taurus or Aquarius",
                },
            },
            "required": ["sign"],
        },
    },
]`;

const stripCommentLines = (text) =>
    text
        .split('\n')
        .filter((line) => !line.trimStart().startsWith('#'))
        .join('\n');

/** Removes trailing commas before `]` / `}` so hand-written JSON still parses. */
const relaxTrailingCommas = (text) => text.replace(/,(\s*[}\]])/g, '$1');

/** The parameters JSON schema: OpenAI `parameters`, or MCP `inputSchema` / `input_schema`. */
const schemaOf = (definition) => definition.parameters ?? definition.inputSchema ?? definition.input_schema ?? null;

const toolFromEntry = (entry) => {
    if (typeof entry === 'string') return { name: entry, description: null };
    if (!entry || typeof entry !== 'object') return null;
    // OpenAI chat-completions style: {type: "function", function: {name, description, parameters}}
    if (entry.function && typeof entry.function === 'object') {
        return {
            name: entry.function.name,
            description: entry.function.description,
            input_schema: schemaOf(entry.function),
        };
    }
    // OpenAI Responses / MCP style: {type: "function", name, description, parameters | inputSchema}
    return { name: entry.name, description: entry.description, input_schema: schemaOf(entry) };
};

/**
 * Parses pasted tool definitions into `[{name, description, input_schema?}]`.
 *
 * Accepts a JSON array or an object with a `tools` array; entries may be
 * `{type: "function", function: {name}}`, `{name, ...}` or plain strings.
 * `#` comment lines and trailing commas are tolerated. A `parameters` /
 * `inputSchema` object is kept as `input_schema` so the execution-URL check
 * can compare it with an MCP server's signature.
 *
 * @returns {{ tools: Array<{name: string, description: string|null, input_schema?: object}>, error: string|null }}
 */
export const parseManualTools = (rawText) => {
    const text = (rawText || '').trim();
    if (!text) return { tools: [], error: null };
    let parsed;
    try {
        parsed = JSON.parse(relaxTrailingCommas(stripCommentLines(text)));
    } catch (_err) {
        return { tools: [], error: 'Tools must be valid JSON' };
    }
    let entries;
    if (Array.isArray(parsed)) entries = parsed;
    else if (parsed && typeof parsed === 'object' && Array.isArray(parsed.tools)) entries = parsed.tools;
    else if (parsed && typeof parsed === 'object' && (parsed.name || parsed.function)) entries = [parsed];
    else {
        return {
            tools: [],
            error: 'Expected a JSON array of tools or an object with a "tools" array',
        };
    }
    const tools = normalizeTools(entries.map(toolFromEntry));
    if (!tools.length) return { tools: [], error: 'No tool names found in the pasted JSON' };
    return { tools, error: null };
};
